import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
  SystemMessage,
  SystemSettings,
} from '../interfaces/system-messages.interface';
import { TranslateService } from '@ngx-translate/core';

/**
 * ⚠️ ALERT: Modified 27/07/2026 by Yaron Hakon for Feature 2530 / Task 2531
 * (system messages Text/TextHe/TextAr only — EnglishText removed; camelCase text* preferred;
 * PascalCase Text* fallback for mixed blobs until republish; blank/whitespace He/Ar → English default).
 */
@Injectable({ providedIn: 'root' })
export class SystemMessagesService {
  private _url = environment.baseUrl;
  systemMessages: SystemMessage[] = [];
  systemSettings: SystemSettings[] = [];
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
  ) {}

  /**
   * Loads system messages and system error messages in parallel and stores
   * the merged list in `systemMessages`. Each endpoint fails independently,
   * so one failing request does not discard the other's messages.
   */
  public fetchSystemMessages(): Observable<SystemMessage[]> {
    return forkJoin([
      this.fetchMessages('system-messages'),
      this.fetchMessages('system-error-messages'),
    ]).pipe(
      map(([messages, errorMessages]) => [...messages, ...errorMessages]),
      tap((messages) => {
        this.systemMessages = messages;
      }),
    );
  }

  private fetchMessages(endpoint: string): Observable<SystemMessage[]> {
    return this.http
      .get<SystemMessage[]>(`${this._url}/CMSetting/${endpoint}`)
      .pipe(
        map((messages) => messages ?? []),
        tap((messages) => this.resolveLocalizedMessages(messages)),
        catchError((err) => {
          console.error(`Failed to load ${endpoint}.`, err);
          return of([]);
        }),
      );
  }

  /** Sets `message` on each entry to the body matching the current language. */
  private resolveLocalizedMessages(messages: SystemMessage[]): void {
    const currentLang = this.translateService.currentLang;
    messages.forEach((message) => {
      const raw = message as SystemMessage & {
        Text?: string;
        TextHe?: string | null;
        TextAr?: string | null;
      };
      const english = this.firstNonBlank(message.text, raw.Text) ?? '';
      const hebrew =
        this.firstNonBlank(message.textHe, raw.TextHe) ?? english;
      const arabic =
        this.firstNonBlank(message.textAr, raw.TextAr) ?? english;
      switch (currentLang) {
        case 'ar':
          message.message = arabic;
          break;
        case 'he':
          message.message = hebrew;
          break;
        case 'en':
        default:
          message.message = english;
          break;
      }
    });
  }

  public fetchSystemSettings(): Observable<SystemSettings[]> {
    return this.http
      .get<SystemSettings[]>(`${this._url}/CMSetting/system-settings`)
      .pipe(
        tap((settings) => {
          this.systemSettings = settings;
        }),
      );
  }

  /** Treats null/undefined/empty/whitespace as missing; returns first non-blank value. */
  private firstNonBlank(
    ...candidates: Array<string | null | undefined>
  ): string | undefined {
    for (const value of candidates) {
      if (value != null && value.trim() !== '') {
        return value;
      }
    }
    return undefined;
  }
}
